/**
 * 지도 뷰(zoom/clat/clng)를 URL 에 적을 때 Next 라우터를 거치지 않는다.
 *
 * Next(app-router.js)는 history.replaceState 를 패치해 ACTION_RESTORE 를 큐에 넣고,
 * 큐는 진행 중인 router.push 를 discarded 로 버린다(app-router-instance.js).
 * 그래서 카메라가 멈춘 뒤 400ms 에 쓰는 뷰 동기화가 "이 지역 검색"·필터 이동을
 * 취소해 버린다. 상태 객체에 __NA 가 있으면 패치가 내부 호출로 보고 원본
 * replaceState 로 직행한다. 기존 history.state(Next 트리 포함)는 spread 로
 * 보존해 popstate 복원을 유지한다.
 */
export function replaceUrlWithoutRouter(url: string): void {
  if (typeof window === "undefined") return;
  window.history.replaceState(
    { ...(window.history.state ?? {}), __NA: true },
    "",
    url,
  );
}

/**
 * 검색 주소를 **얕게** 바꾼다 — Next 가 패치한 replaceState 를 그대로 탄다(09-29).
 *
 * replaceUrlWithoutRouter 와 반대로 __NA 를 붙이지 않는다. 그러면 패치가 ACTION_RESTORE
 * 를 보내는데, 트리는 지금 history.state 의 것(= 지금 화면)이라 서버 요청도 세그먼트
 * 재마운트도 없이 주소(canonicalUrl)만 바뀌고 useSearchParams·usePathname 이 따라온다
 * (next/dist/client/components/app-router.js · restore-reducer.js). 경로 이름이 바뀌어도
 * (/s/seoul → /s/tokyo) 트리가 그대로라 [region] 이 새로 마운트되지 않는다 — 대신
 * useParams·서버 props 는 옛 값에 머문다. 결과를 주소에 맞추는 건 호출자 몫이다.
 * 진행 중인 router.push 는 버려지는데, 이 이동은 사용자가 방금 누른 것이라 괜찮다.
 */
export function replaceSearchUrlShallow(url: string): void {
  if (typeof window === "undefined") return;
  window.history.replaceState(null, "", url);
}

export type ParamSource = URLSearchParams | { get(key: string): string | null };

/**
 * 핸들러에서 "지금 주소창의" 파라미터. useSearchParams 는 replaceUrlWithoutRouter 로
 * 쓴 뷰 파라미터를 모르므로(라우터를 거치지 않아 리렌더가 없다), 새 URL 을 만들 때는
 * 이걸 쓴다. 렌더 중에는 쓰지 않는다 — SSR 과 값이 갈려 hydration 이 깨진다.
 */
export function liveSearchParams(fallback: ParamSource): ParamSource {
  if (typeof window === "undefined") return fallback;
  return new URLSearchParams(window.location.search);
}
